import { db } from "./firebase";
import {
    collection, query, where, orderBy, limit,
    getDocs, doc, getDoc
} from "firebase/firestore";
import { COLLECTIONS } from "./collections";
import { automationLogFromFirestore } from "../models/automationLog/automationLogConverter";

/**
 * AutomationLogService
 * 
 * Serviço somente-leitura para consultar logs de envio de automações.
 * Os logs são ESCRITOS pelas Cloud Functions, o frontend apenas LÊ.
 */
class AutomationLogService {

    /**
     * Retorna a referência da subcoleção de logs para o tenant.
     */
    _collRef(idTenant) {
        return collection(db, "tenants", idTenant, COLLECTIONS.AUTOMATION_LOGS);
    }

    /**
     * Busca os logs de um gatilho específico.
     * @param {string} idTenant 
     * @param {string} triggerId - Ex: "trial_scheduled"
     * @param {number} maxResults - Quantidade máxima de logs (default: 50)
     * @returns {Promise<Array>} Lista de logs ordenados por data (mais recente primeiro)
     */
    async getLogsByTrigger(idTenant, triggerId, maxResults = 50) {
        const q = query(
            this._collRef(idTenant),
            where("trigger", "==", triggerId),
            orderBy("createdAt", "desc"),
            limit(maxResults)
        );
        const snap = await getDocs(q);
        return snap.docs.map(d => automationLogFromFirestore(d)).filter(Boolean);
    }

    /**
     * Busca os logs de um destinatário específico (member ou employee).
     * @param {string} idTenant
     * @param {string} recipientId
     * @param {number} maxResults
     */
    async getLogsByRecipient(idTenant, recipientId, maxResults = 50) {
        const q = query(
            this._collRef(idTenant),
            where("recipientId", "==", recipientId),
            orderBy("createdAt", "desc"),
            limit(maxResults)
        );
        const snap = await getDocs(q);
        return snap.docs.map(d => automationLogFromFirestore(d)).filter(Boolean);
    }

    /**
     * Busca um log específico por ID.
     */
    async getById(idTenant, logId) {
        const ref = doc(db, "tenants", idTenant, COLLECTIONS.AUTOMATION_LOGS, logId);
        const snap = await getDoc(ref);
        return automationLogFromFirestore(snap);
    }

    /**
     * Contadores rápidos para um gatilho: total, enviados, falhas.
     */
    async getStatsByTrigger(idTenant, triggerId) {
        const logs = await this.getLogsByTrigger(idTenant, triggerId, 200);
        return {
            total: logs.length,
            sent: logs.filter(l => ["SENT", "DELIVERED", "READ"].includes(l.status)).length,
            delivered: logs.filter(l => ["DELIVERED", "READ"].includes(l.status)).length,
            read: logs.filter(l => l.status === "READ").length,
            failed: logs.filter(l => l.status === "FAILED").length,
            queued: logs.filter(l => l.status === "QUEUED").length,
        };
    }
}

const automationLogService = new AutomationLogService();
export default automationLogService;
